/**
 * Agent Grid Component
 *
 * Displays agents in a responsive grid layout.
 */

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';

export default function AgentGrid({ agents, onAgentClick, onConfigureClick, onTestClick }) {
  const getAgentIcon = (category) => {
    // Return appropriate icon based on category
    return <SmartToyIcon />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      product: 'primary',
      design: 'secondary',
      architecture: 'info',
      development: 'success',
      testing: 'warning',
      devops: 'error',
      security: 'default',
      documentation: 'primary',
      analytics: 'secondary',
      compliance: 'info'
    };
    return colors[category] || 'default';
  };

  const getModelBadge = (model) => {
    if (model && model.includes('sonnet')) {
      return { label: 'Sonnet-4', color: 'primary' };
    } else if (model && model.includes('haiku')) {
      return { label: 'Haiku', color: 'success' };
    }
    return { label: 'AI', color: 'default' };
  };

  if (!agents || agents.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No agents found
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {agents.map((agent) => {
        const modelBadge = getModelBadge(agent.default_config?.model);

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent.agent_id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}
              onClick={() => onAgentClick(agent)}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    {getAgentIcon(agent.category)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" noWrap>
                      {agent.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {agent.agent_id}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {agent.category && (
                    <Chip
                      label={agent.category}
                      size="small"
                      color={getCategoryColor(agent.category)}
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={modelBadge.label}
                    size="small"
                    color={modelBadge.color}
                  />
                </Box>

                {agent.description && (
                  <Typography variant="body2" color="text.secondary" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '3.6em'
                  }}>
                    {agent.description}
                  </Typography>
                )}

                {agent.supported_tasks && agent.supported_tasks.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Supports: {agent.supported_tasks.slice(0, 3).join(', ')}
                      {agent.supported_tasks.length > 3 && '...'}
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfigureClick(agent);
                  }}
                >
                  Configure
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTestClick(agent);
                  }}
                >
                  Test
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
